import React, { FC, useState, ChangeEvent } from 'react';
import { Order } from 'models/order';
import { Box, Button, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { trackingLinkTemplate } from 'apollo/templates/order';

type AddTrackingLinkProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order?: Order;
};

const AddTrackingLink: FC<AddTrackingLinkProps> = ({ onUpdate, onClose, open, order }: AddTrackingLinkProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [transportTrackingLink, setTransportTrackingLink] = useState('');
    const [isValid, setIsValid] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const handleTrackingLinkBox = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const regEx =
            /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
        setTransportTrackingLink(value);
        setIsValid(regEx.test(value));
    };

    const [changeStatus] = useMutation(trackingLinkTemplate, {
        onCompleted: ({ trackingLink }) => {
            onClose();
            onUpdate?.({ ...order, ...trackingLink });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChangeStatus = async () => {
        await changeStatus({
            variables: {
                orderId: order?._id,
                transportTrackingLink: transportTrackingLink,
            },
        });
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:TRACKING_LINK')}
            actions={
                <Button
                    disabled={!isValid || !transportTrackingLink}
                    variant="contained"
                    onClick={handleChangeStatus}
                    data-test="add-tracking-link.save-btn"
                >
                    {translate('orders:SAVE_THE_LINK')}
                </Button>
            }
        >
            <Box mb={1}>
                <TextField
                    fullWidth
                    multiline
                    placeholder={translate('orders:ADD_TRACKING_LINK')}
                    value={transportTrackingLink}
                    onChange={handleTrackingLinkBox}
                    data-test="add-tracking-link.input"
                />
            </Box>
        </DHDialog>
    );
};

export default AddTrackingLink;
